import {
  createElement,
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type FC,
  type ReactElement,
} from "react";
import { utcIsoToEpochMs } from "@zzk/core";
import {
  Badge,
  DashboardLayout,
  EmptyState,
  ErrorState,
  LineChart,
  Skeleton,
  type BadgeProps,
  type SkeletonProps,
} from "@zzk/ui";

import type {
  BiTimeseriesMetric,
  CachedBiFailureReasonBreakdown,
  CachedBiHealth,
  CachedBiTimeseries,
  CachedOverview,
} from "../../../integration/zzk-sec.inbound.js";
import type { CachedMemoryHealth } from "../../../integration/zzk-mem.inbound.js";

import { OverviewCard } from "./overview-card.js";
import { RefreshButton } from "./refresh-button.js";
import { ApiCoveragePanel } from "./api-coverage-panel.js";

const TIMESERIES_METRICS: readonly {
  readonly metric: BiTimeseriesMetric;
  readonly label: string;
}[] = [
  { metric: "active-devices", label: "Devices ativos" },
  { metric: "refreshes", label: "Refreshes" },
  { metric: "enrollments", label: "Enrollments" },
  { metric: "audit", label: "Audit events" },
];

const UiBadge = Badge as FC<BadgeProps>;
const UiSkeleton = Skeleton as FC<SkeletonProps>;

type SignalTone = "neutral" | "success" | "warning" | "danger" | "info";

interface OperationalData {
  readonly health: CachedBiHealth | null;
  readonly timeseries: Partial<Record<BiTimeseriesMetric, CachedBiTimeseries>>;
  readonly failureReasonBreakdown: CachedBiFailureReasonBreakdown | null;
  readonly memoryHealth: CachedMemoryHealth | null;
  readonly degraded: readonly string[];
}

interface OverviewState {
  readonly status: "loading" | "ready" | "error";
  readonly overview: CachedOverview | null;
  readonly operational: OperationalData;
  readonly error: string | null;
}

const emptyOperationalData: OperationalData = {
  health: null,
  timeseries: {},
  failureReasonBreakdown: null,
  memoryHealth: null,
  degraded: [],
};

export interface OverviewViewProps {
  readonly initialOverview?: CachedOverview | null;
}

export function OverviewView(props: OverviewViewProps = {}): ReactElement {
  const [state, setState] = useState<OverviewState>(() => ({
    status: props.initialOverview ? "ready" : "loading",
    overview: props.initialOverview ?? null,
    operational: emptyOperationalData,
    error: null,
  }));

  const fetchOverview = useCallback(
    async (bypassCache: boolean, signal?: AbortSignal): Promise<void> => {
      setState((prev) => ({
        status: prev.overview ? prev.status : "loading",
        overview: prev.overview,
        operational: prev.operational,
        error: null,
      }));

      const snapshot = await loadOperationalSnapshot(bypassCache, signal);
      if (signal?.aborted) return;

      setState((prev) => {
        const overview =
          snapshot.overview.status === "fulfilled"
            ? snapshot.overview.value
            : prev.overview;
        const primaryError =
          snapshot.overview.status === "rejected"
            ? friendlyErrorFromUnknown(snapshot.overview.reason)
            : null;

        if (!overview) {
          return {
            status: "error",
            overview: null,
            operational: emptyOperationalData,
            error: primaryError ?? "Não foi possível carregar os KPIs.",
          };
        }

        return {
          status: "ready",
          overview,
          operational: {
            health: fulfilledOrNull(snapshot.health) ?? prev.operational.health,
            timeseries: {
              ...prev.operational.timeseries,
              ...fulfilledTimeseries(snapshot.timeseries),
            },
            failureReasonBreakdown:
              fulfilledOrNull(snapshot.failureReasonBreakdown) ??
              prev.operational.failureReasonBreakdown,
            memoryHealth:
              fulfilledOrNull(snapshot.memoryHealth) ??
              prev.operational.memoryHealth,
            degraded: snapshot.degraded,
          },
          error: primaryError,
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (props.initialOverview) return undefined;
    const controller = new AbortController();
    void fetchOverview(false, controller.signal);
    return () => controller.abort();
  }, [fetchOverview, props.initialOverview]);

  const loading = state.status === "loading" && !state.overview;

  return createElement(
    "section",
    {
      "data-testid": "overview-view",
      "data-status": state.status,
    },
    createElement(DashboardLayout, {
      title: "Operação zzk-sec",
      subtitle: "Saúde, atividade e falhas do control-plane de identidade.",
      actions: createElement(
        "div",
        { "data-testid": "overview-actions" },
        createElement(RefreshButton, {
          onRefresh: () => fetchOverview(true),
          disabled: state.status === "loading",
        }),
      ),
      children: createElement("div", { className: "zzk-sec-console" }, [
        renderDegradedNotice(state),
        createElement(OverviewCard, {
          key: "card",
          overview: state.overview,
          error: state.status === "error" ? state.error : null,
          loading,
        }),
        renderOperationalPanels(state.operational, loading),
      ]),
    }),
  );
}

export function createOverviewView(
  initialOverview?: CachedOverview | null,
): ReactElement {
  return createElement(OverviewView as FC<OverviewViewProps>, {
    initialOverview,
  });
}

function renderOperationalPanels(
  data: OperationalData,
  loading: boolean,
): ReactElement {
  if (loading) {
    return createElement(
      "div",
      {
        key: "ops-loading",
        className: "zzk-sec-skeleton-grid",
        "data-testid": "overview-operational-loading",
      },
      [
        createElement(UiSkeleton, { key: "health", className: "h-24" }),
        createElement(UiSkeleton, { key: "trend-a", className: "h-56" }),
        createElement(UiSkeleton, { key: "trend-b", className: "h-56" }),
        createElement(UiSkeleton, { key: "failures", className: "h-56" }),
      ],
    );
  }

  if (
    !data.health &&
    Object.keys(data.timeseries).length === 0 &&
    !data.failureReasonBreakdown &&
    !data.memoryHealth
  ) {
    return createElement(EmptyState, {
      title: "Sem sinais operacionais",
      message: "Nenhuma leitura operacional foi carregada ainda.",
    });
  }

  return createElement(
    "div",
    {
      key: "ops",
      className: "zzk-sec-operational-console",
      "data-testid": "overview-operational-console",
    },
    [
      renderHealthBand(data.health),
      renderMemoryHealth(data.memoryHealth),
      createElement(ApiCoveragePanel, { key: "api-coverage" }),
      createElement(
        "div",
        { key: "workbench", className: "zzk-sec-workbench-grid" },
        [
          renderTimeseriesGrid(data.timeseries),
          renderFailureReasons(data.failureReasonBreakdown),
        ],
      ),
    ],
  );
}

function renderDegradedNotice(state: OverviewState): ReactElement | null {
  const degraded = state.operational.degraded;
  if (!state.error && degraded.length === 0) return null;

  const message = state.error
    ? state.error
    : `Dados parciais: ${degraded.join(", ")}.`;

  return createElement(
    "div",
    {
      key: "degraded",
      className: "zzk-sec-alert",
      "data-testid": "overview-degraded",
      role: "status",
    },
    createElement(UiBadge, { tone: "warning", children: message }),
  );
}

function renderMemoryHealth(health: CachedMemoryHealth | null): ReactElement {
  if (!health) {
    return createElement(
      "section",
      {
        key: "memory-health",
        className: "zzk-sec-section zzk-mem-health-panel",
        "data-testid": "memory-health-panel",
      },
      createElement(ErrorState, {
        title: "Memória indisponível",
        message: "Não foi possível carregar a saúde epistêmica do zzk-mem.",
      }),
    );
  }

  const painTotal = sumRecordValues(health.data.pain_per_type);
  const citeRate = formatPercent(health.data.cite_rate);
  const vetoRate = formatPercent(health.data.veto_override_rate);
  const tone = memoryHealthTone(health, painTotal);

  return createElement("section", {
    key: "memory-health",
    className: "zzk-sec-section zzk-mem-health-panel",
    "data-testid": "memory-health-panel",
    "data-tone": tone,
    "aria-label": "Saúde epistêmica do zzk-mem",
    children: [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-section-header" },
        [
          createElement(
            "div",
            { key: "title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "zzk-mem",
              ),
              createElement(
                "h2",
                { key: "heading", className: "zzk-sec-section-title" },
                "Saúde epistêmica da memória",
              ),
            ],
          ),
          createElement(
            "span",
            { key: "meta", className: "zzk-sec-muted" },
            `Leitura ${health.kind}; TTL ${health.ttl_seconds}s`,
          ),
        ],
      ),
      createElement(
        "div",
        { key: "cards", className: "zzk-sec-ops-grid zzk-mem-health-grid" },
        [
          renderSignalCard({
            key: "typed",
            label: "Memórias tipadas",
            value: formatNumber(health.data.memory_growth),
            tone: health.data.memory_growth > 0 ? "info" : "neutral",
            hint: `Citações ${formatNumber(health.data.cite_growth)}`,
          }),
          renderSignalCard({
            key: "cite-rate",
            label: "Cite rate",
            value: citeRate,
            tone: health.data.cite_rate >= 0.25 ? "success" : "warning",
            hint: "Baixo cite rate indica memória virando swamp.",
          }),
          renderSignalCard({
            key: "veto-rate",
            label: "Veto override",
            value: vetoRate,
            tone: health.data.veto_override_rate > 0.5 ? "warning" : "success",
            hint: `${formatNumber(health.data.veto_events_total)} eventos de veto`,
          }),
          renderSignalCard({
            key: "pain",
            label: "Pain reports",
            value: formatNumber(painTotal),
            tone: painTotal > 0 ? "warning" : "success",
            hint: `Arquivadas ${formatPercent(health.data.archived_rate)}`,
          }),
        ],
      ),
      renderMemoryTypeDistribution(health),
    ],
  });
}

function renderMemoryTypeDistribution(health: CachedMemoryHealth): ReactElement {
  const total = Math.max(health.data.memory_growth, 1);
  const rows = Object.entries(health.data.type_distribution)
    .sort(([, left], [, right]) => right - left)
    .slice(0, 6);

  if (rows.length === 0) {
    return createElement(EmptyState, {
      title: "Sem memória tipada",
      message: "O zzk-mem ainda não retornou memórias epistêmicas tipadas.",
    });
  }

  return createElement(
    "div",
    {
      key: "distribution",
      className: "zzk-mem-distribution",
      "data-testid": "memory-type-distribution",
    },
    rows.map(([type, count]) => {
      const share = count / total;
      return createElement(
        "div",
        { key: type, className: "zzk-mem-distribution-row" },
        [
          createElement(
            "div",
            { key: "line", className: "zzk-sec-failure-line" },
            [
              createElement(
                "span",
                { key: "name", className: "zzk-sec-failure-name" },
                `${type}: ${formatNumber(count)}`,
              ),
              createElement(
                "span",
                { key: "share", className: "zzk-sec-muted" },
                formatPercent(share),
              ),
            ],
          ),
          createElement(
            "div",
            {
              key: "bar",
              className: "zzk-sec-failure-bar zzk-mem-distribution-bar",
              style: {
                "--zzk-sec-bar-width": `${Math.round(share * 100)}%`,
              } as CSSProperties,
            },
            createElement("span", null),
          ),
        ],
      );
    }),
  );
}

function renderHealthBand(health: CachedBiHealth | null): ReactElement {
  if (!health) {
    return createElement(
      "section",
      { key: "health", className: "zzk-sec-section" },
      createElement(ErrorState, {
        title: "Saúde indisponível",
        message: "Não foi possível carregar os motivos de denial.",
      }),
    );
  }

  const lastHourTotal = sumReasonCounts(health.data.lastHour);
  const last24hTotal = sumReasonCounts(health.data.last24Hours);
  const tone = lastHourTotal > 0 ? "warning" : "success";

  return createElement("section", {
    key: "health",
    className: "zzk-sec-section",
    "aria-label": "Saúde operacional",
    children: [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-section-header" },
        [
          createElement(
            "div",
            { key: "title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Saúde operacional",
              ),
              createElement(
                "h2",
                { key: "heading", className: "zzk-sec-section-title" },
                "Denials e estado do control-plane",
              ),
            ],
          ),
          createElement(
            "span",
            { key: "meta", className: "zzk-sec-muted" },
            `Leitura ${health.kind}; TTL ${health.ttl_seconds}s`,
          ),
        ],
      ),
      createElement(
        "div",
        { key: "cards", className: "zzk-sec-ops-grid" },
        [
          renderSignalCard({
            key: "last-hour",
            label: "Denials 1h",
            value: formatNumber(lastHourTotal),
            tone,
            hint: mostFrequentReason(health.data.lastHour),
          }),
          renderSignalCard({
            key: "last-day",
            label: "Denials 24h",
            value: formatNumber(last24hTotal),
            tone: last24hTotal > 0 ? "warning" : "success",
            hint: mostFrequentReason(health.data.last24Hours),
          }),
          renderSignalCard({
            key: "status",
            label: "Estado",
            value: lastHourTotal > 0 ? "Degradado" : "Estável",
            tone,
            hint: `Atualizado ${formatPtBrDateTime(health.fetched_at)}`,
          }),
        ],
      ),
    ],
  });
}

function renderSignalCard(input: {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  readonly tone: SignalTone;
}): ReactElement {
  return createElement(
    "article",
    {
      key: input.key,
      className: "zzk-sec-signal-card",
      "data-tone": input.tone,
    },
    [
      createElement("span", { key: "label" }, input.label),
      createElement("strong", { key: "value" }, input.value),
      createElement("small", { key: "hint" }, input.hint),
    ],
  );
}

function renderTimeseriesGrid(
  timeseries: Partial<Record<BiTimeseriesMetric, CachedBiTimeseries>>,
): ReactElement {
  return createElement(
    "section",
    {
      key: "timeseries",
      className: "zzk-sec-section",
      "data-testid": "timeseries-grid",
    },
    [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-section-header" },
        [
          createElement(
            "div",
            { key: "title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Tendências",
              ),
              createElement(
                "h2",
                { key: "heading", className: "zzk-sec-section-title" },
                "Atividade por hora",
              ),
            ],
          ),
          createElement(
            "span",
            { key: "meta", className: "zzk-sec-muted" },
            "Bucket horário",
          ),
        ],
      ),
      createElement(
        "div",
        { key: "grid", className: "zzk-sec-trend-grid" },
        TIMESERIES_METRICS.map(({ metric, label }) => {
          const series = timeseries[metric];
          return createElement(LineChart, {
            key: metric,
            id: `sec-${metric}`,
            title: label,
            dataset:
              series?.data.points.map((point) => ({
                bucket: formatEpochHour(point.ts),
                count: point.count,
              })) ?? [],
            xField: "bucket",
            yField: "count",
            series: [{ id: "count", label, yField: "count" }],
            tone: metric === "active-devices" ? "info" : "neutral",
            error: series ? null : "Série indisponível.",
            height: 210,
            className: "zzk-sec-chart",
          });
        }),
      ),
    ],
  );
}

function renderFailureReasons(
  breakdown: CachedBiFailureReasonBreakdown | null,
): ReactElement {
  if (!breakdown) {
    return createElement(
      "section",
      { key: "failure-reasons", className: "zzk-sec-section" },
      createElement(ErrorState, {
        title: "Falhas indisponíveis",
        message: "Não foi possível carregar o ranking de motivos.",
      }),
    );
  }

  if (breakdown.data.data.length === 0) {
    return createElement(
      "section",
      { key: "failure-reasons", className: "zzk-sec-section" },
      createElement(EmptyState, {
        title: "Sem falhas no período",
        message: "O zzk-sec não retornou denials para a janela atual.",
      }),
    );
  }

  const maxCount = Math.max(...breakdown.data.data.map((item) => item.count), 1);

  return createElement(
    "section",
    {
      key: "failure-reasons",
      className: "zzk-sec-failure-panel",
      "data-testid": "failure-reason-breakdown",
      "data-window": breakdown.data.window,
    },
    [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-section-header" },
        [
          createElement(
            "div",
            { key: "title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Risco",
              ),
              createElement(
                "h2",
                { key: "heading", className: "zzk-sec-section-title" },
                "Motivos de falha",
              ),
            ],
          ),
          createElement(
            "span",
            { key: "meta", className: "zzk-sec-muted" },
            `Janela ${breakdown.data.window}`,
          ),
        ],
      ),
      createElement(
        "ol",
        { key: "list", className: "zzk-sec-failure-list" },
        breakdown.data.data.map((item) => {
          const width = `${Math.round((item.count / maxCount) * 100)}%`;
          return createElement(
            "li",
            {
              key: item.dimension,
              className: "zzk-sec-failure-row",
              "data-testid": "failure-reason-row",
            },
            [
              createElement(
                "div",
                { key: "line", className: "zzk-sec-failure-line" },
                [
                  createElement(
                    "span",
                    { key: "name", className: "zzk-sec-failure-name" },
                    `${item.dimension}: ${item.count}`,
                  ),
                  createElement(
                    "span",
                    { key: "share", className: "zzk-sec-muted" },
                    `${Math.round((item.count / maxCount) * 100)}%`,
                  ),
                ],
              ),
              createElement(
                "div",
                {
                  key: "bar",
                  className: "zzk-sec-failure-bar",
                  style: {
                    "--zzk-sec-bar-width": width,
                  } as CSSProperties,
                },
                createElement("span", null),
              ),
            ],
          );
        }),
      ),
    ],
  );
}

async function loadOperationalSnapshot(
  bypassCache: boolean,
  signal?: AbortSignal,
) {
  const query = bypassCache ? "?bypass_cache=1" : "";
  const overview = fetchLensData<CachedOverview>(
    `/api/lens/sec/overview${query}`,
    signal,
  );
  const health = fetchLensData<CachedBiHealth>(
    `/api/lens/sec/health${query}`,
    signal,
  );
  const failureReasonBreakdown = fetchLensData<CachedBiFailureReasonBreakdown>(
    `/api/lens/sec/breakdowns/failure-reason${query}`,
    signal,
  );
  const memoryHealth = fetchLensData<CachedMemoryHealth>(
    "/api/lens/mem/memory-health",
    signal,
  );
  const timeseries = Object.fromEntries(
    await Promise.all(
      TIMESERIES_METRICS.map(async ({ metric }) => {
        const request = fetchLensData<CachedBiTimeseries>(
          `/api/lens/sec/timeseries/${metric}${query}`,
          signal,
        );
        try {
          return [
            metric,
            {
              status: "fulfilled",
              value: await request,
            } satisfies PromiseFulfilledResult<CachedBiTimeseries>,
          ] as const;
        } catch (reason) {
          return [
            metric,
            {
              status: "rejected",
              reason,
            } satisfies PromiseRejectedResult,
          ] as const;
        }
      }),
    ),
  ) as Record<BiTimeseriesMetric, PromiseSettledResult<CachedBiTimeseries>>;

  const [
    overviewResult,
    healthResult,
    failureResult,
    memoryHealthResult,
  ] = await Promise.allSettled([
    overview,
    health,
    failureReasonBreakdown,
    memoryHealth,
  ]);

  return {
    overview: overviewResult,
    health: healthResult,
    failureReasonBreakdown: failureResult,
    memoryHealth: memoryHealthResult,
    timeseries,
    degraded: collectDegraded(
      healthResult,
      failureResult,
      memoryHealthResult,
      timeseries,
    ),
  };
}

async function fetchLensData<T>(
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: { accept: "application/json" },
    signal,
  });
  if (!response.ok) throw new HttpStatusError(response.status);
  const body = (await response.json()) as { data: T };
  return body.data;
}

function fulfilledOrNull<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === "fulfilled" ? result.value : null;
}

function fulfilledTimeseries(
  timeseries: Record<BiTimeseriesMetric, PromiseSettledResult<CachedBiTimeseries>>,
): Partial<Record<BiTimeseriesMetric, CachedBiTimeseries>> {
  const values: Partial<Record<BiTimeseriesMetric, CachedBiTimeseries>> = {};
  for (const metric of TIMESERIES_METRICS.map((item) => item.metric)) {
    const result = timeseries[metric];
    if (result.status === "fulfilled") values[metric] = result.value;
  }
  return values;
}

function collectDegraded(
  health: PromiseSettledResult<CachedBiHealth>,
  failureReasonBreakdown: PromiseSettledResult<CachedBiFailureReasonBreakdown>,
  memoryHealth: PromiseSettledResult<CachedMemoryHealth>,
  timeseries: Record<BiTimeseriesMetric, PromiseSettledResult<CachedBiTimeseries>>,
): string[] {
  const degraded: string[] = [];
  if (health.status === "rejected") degraded.push("health");
  if (failureReasonBreakdown.status === "rejected") {
    degraded.push("failure reasons");
  }
  if (memoryHealth.status === "rejected") degraded.push("zzk-mem");
  for (const { metric, label } of TIMESERIES_METRICS) {
    if (timeseries[metric].status === "rejected") degraded.push(label);
  }
  return degraded;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatPtBrDateTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(utcIsoToEpochMs(value));
}

function formatEpochHour(value: number): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(value);
}

function sumReasonCounts(
  reasons: readonly { readonly count: number }[],
): number {
  return reasons.reduce((sum, item) => sum + item.count, 0);
}

function sumRecordValues(values: Readonly<Record<string, number>>): number {
  return Object.values(values).reduce((sum, item) => sum + item, 0);
}

function memoryHealthTone(
  health: CachedMemoryHealth,
  painTotal: number,
): SignalTone {
  if (health.data.veto_override_rate > 0.5 || painTotal > 0) return "warning";
  if (health.data.memory_growth === 0) return "neutral";
  return "success";
}

function mostFrequentReason(
  reasons: readonly { readonly reason: string; readonly count: number }[],
): string {
  const first = reasons[0];
  return first ? `${first.reason}: ${first.count}` : "Sem denials.";
}

function friendlyErrorFromUnknown(error: unknown): string {
  if (error instanceof HttpStatusError) {
    if (error.status === 503) return "Serviço temporariamente indisponível.";
    if (error.status === 401) return "Sessão expirada. Faça login novamente.";
  }
  return "Não foi possível carregar os KPIs.";
}

class HttpStatusError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${status}`);
    this.name = "HttpStatusError";
  }
}

export { OverviewCard } from "./overview-card.js";
export { RefreshButton } from "./refresh-button.js";
